import React, { memo, useCallback, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useFormik } from 'formik'
import {
  Typography,
  Upload,
  Popconfirm,
  Tooltip,
  Button,
  message,
  Tag,
} from 'antd'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import moment from 'moment'
import {
  ASSIGNEE_TYPE,
  DIGITAL_SIGN_PROVIDER,
  DIGITAL_TYPE_SIGN_SYSTEM,
  DOCUMENT_TYPE,
} from '../../constants'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AiFillBank, AiOutlineTeam, AiOutlineUser } from 'react-icons/ai'
import { AvatarHasName } from '../../components/common/Avatar'
import SelectPeoplePopup from '../../components/SelectPeoplePopup/SelectPeoplePopup'
import SelectGroupPopup from '../../components/SelectGroupPopup/SelectGroupPopup'
import AuthoritiesActionModal from '../InternalDocumentIncoming/AuthoritiesActionModal'
import {
  SelectList,
  UploadFileListItem,
  UploadFileListWrapper,
} from '../InternalDocumentIncoming/InternalDocumentIncomingDocCreatePageStyled'
import {
  DeleteOutlined,
  FileTextOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import utils from '../../utils'
import PopupRenameFile from '../../components/PopupRenameFile/PopupRenameFile'
import * as yup from 'yup'
import dayjs from 'dayjs'

const InternalConnectedDocumentIncomingCreate = props => {
  const {
    open,
    handleClose,
    authorityStore,
    selectPeopleStore,
    bookStore,
    loadingAnimationStore,
    fileStore,
    signedDocumentStore,
    internalDocumentStore,
    selectedInternalConnectedDocumentIncoming,
    createMessage,
  } = props
  const navigate = useNavigate()
  const { authorityIssuedsList } = authorityStore
  const [handlerList, setHandlerList] = useState([])
  const [combinerList, setCombinerList] = useState([])
  const [followerList, setFollowerList] = useState([])
  const [fileList, setFileList] = useState([])
  const [fileFromDocumentList, setFileFromDocumentList] = useState([])

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectGroupPopup,
    setIsVisibleSelectGroupPopup,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    handleOpenSelectGroupPopup,
    handleCancelSelectGroup,
    clearSelectPeopleStore,
    clearSelected,
  } = selectPeopleStore
  const { bookGroupList, bookListOfGroup } = bookStore
  const { Title } = Typography
  const { createInternalDocumentIncoming } = internalDocumentStore
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [onlyOne, setOnlyOneUser] = useState('')

  const formik = useFormik({
    initialValues: {
      authority_issued_id: undefined,
      assign_user: {
        assignees: [],
        start_date: undefined,
        end_date: undefined,
      },
      attachments: [],
      book_group_id: undefined,
      book_id: undefined,
      date_issued: selectedInternalConnectedDocumentIncoming?.dateIssue,
      document_number: undefined,
      document_type: null,
      incoming_number: undefined,
      outgoing_date: dayjs(),
      sign_date: null,
      signer: selectedInternalConnectedDocumentIncoming?.signer,
      title: selectedInternalConnectedDocumentIncoming?.title,
      urgency_level: undefined,
    },
    validationSchema: yup.object().shape({
      document_number: yup
        .string()
        .required('Vui lòng nhập số hiệu!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập số hiệu!'),
      incoming_number: yup
        .string()
        .required('Vui lòng nhập số đến!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập số đến!'),
      title: yup
        .string()
        .required('Vui lòng nhập trích yếu!')
        .matches(/^(?=.*\S).+$/, 'Vui lòng nhập trích yếu!'),
      outgoing_date: yup.string().required('Vui lòng chọn ngày đến!'),
      attachments: yup.array().min(1, 'Vui lòng chọn tài liệu đính kèm!'),
    }),
    onSubmit: async values => {
      setButtonBlock(true)
      try {
        loadingAnimationStore.showSpinner(true)
        await createInternalDocumentIncoming(values)
        loadingAnimationStore.showSpinner(false)
        navigate('/internal-document/incoming-document')
        message.success('Tạo văn bản thành công!')
      } catch (err) {
        console.log(err)
      } finally {
        handleCloseForm()
      }
    },
  })

  const handleSelectBookGroupId = async bookGroupId => {
    if (!bookGroupId) createMessage('Chưa có nhóm sổ văn bản!', true, 'warning')
    formik.setFieldValue('book_group_id', bookGroupId)
    loadingAnimationStore.showSpinner(true)
    try {
      const bookListOfGroup = await bookStore.getBookByBookGroupId(bookGroupId)
      formik.setFieldValue('book_id', bookListOfGroup.data[0].id)
    } catch (error) {
      createMessage(error?.vi, true, 'error')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleSelectBookId = bookId => {
    if (!bookId) createMessage('Chưa có sổ văn bản!', true, 'warning')
    formik.setFieldValue('book_id', bookId)
  }

  useEffect(() => {
    setHandlerList([
      ...selectUserData[ASSIGNEE_TYPE.HANDLER],
      ...selectDepartmentData[ASSIGNEE_TYPE.HANDLER],
      ...selectGroupData[ASSIGNEE_TYPE.HANDLER],
    ])
    setFollowerList([
      ...selectUserData[ASSIGNEE_TYPE.FOLLOWER],
      ...selectDepartmentData[ASSIGNEE_TYPE.FOLLOWER],
      ...selectGroupData[ASSIGNEE_TYPE.FOLLOWER],
    ])
    setCombinerList([
      ...selectUserData[ASSIGNEE_TYPE.COMBINER],
      ...selectDepartmentData[ASSIGNEE_TYPE.COMBINER],
      ...selectGroupData[ASSIGNEE_TYPE.COMBINER],
    ])
  }, [selectUserData, selectDepartmentData, selectGroupData])

  const updateAssignees = useCallback(() => {
    const newAssignees = [...followerList, ...handlerList, ...combinerList].map(
      el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: el.permission,
      })
    )
    formik.setFieldValue('assign_user', {
      assignees: newAssignees,
      end_date: endDate,
      start_date: startDate,
    })
  }, [followerList, handlerList, combinerList, endDate, startDate])
  const updateAttachments = useCallback(async () => {
    setFileFromDocumentList(
      selectedInternalConnectedDocumentIncoming?.attachments
    )
    const uploadFileList = fileList.map(file => file.originFileObj)
    const batchUploadArr = []
    uploadFileList.forEach(file => {
      const formData = new FormData()
      formData.append('file', file)
      batchUploadArr.push(fileStore.uploadFile(formData))
    })
    const response = await Promise.all(batchUploadArr)
    const res = selectedInternalConnectedDocumentIncoming?.attachments
      ? response
          .map(response => response.data.file_id)
          .concat(
            selectedInternalConnectedDocumentIncoming?.attachments?.map(
              file => file.id
            )
          )
      : response.map(response => response.data.file_id)

    formik.setFieldValue('attachments', res)
  }, [fileList, selectedInternalConnectedDocumentIncoming])

  useEffect(() => {
    updateAttachments()
    updateAssignees()
  }, [updateAttachments, updateAssignees])

  useEffect(() => {
    if (!open) {
      formik.setFieldValue(
        'signer',
        selectedInternalConnectedDocumentIncoming?.signer
      )
      formik.setFieldValue(
        'title',
        selectedInternalConnectedDocumentIncoming?.title
      )
      formik.setFieldValue('outgoing_date', dayjs())
      formik.setFieldValue(
        'date_issued',
        selectedInternalConnectedDocumentIncoming?.dateIssue
      )
    }
  }, [open])
  useEffect(() => {
    formik.setFieldValue(
      'signer',
      selectedInternalConnectedDocumentIncoming?.signer
    )
    formik.setFieldValue(
      'title',
      selectedInternalConnectedDocumentIncoming?.title
    )
    formik.setFieldValue('outgoing_date', dayjs())
    formik.setFieldValue(
      'date_issued',
      selectedInternalConnectedDocumentIncoming?.dateIssue
    )
  }, [selectedInternalConnectedDocumentIncoming])

  const [advanceFilterID, setAdvanceFilterID] = useState({
    date_by: null,
    month: '',
    tag: [],
  })
  useEffect(() => {
    setAdvanceFilterID({
      ...advanceFilterDI,
      month: dayjs().format('YYYY-MM-DD'),
    })
  }, [selectedInternalConnectedDocumentIncoming])

  const [advanceFilterDI, setAdvanceFilterDI] = useState({
    date_by: null,
    month: '',
    tag: [],
  })
  useEffect(() => {
    setAdvanceFilterDI({
      ...advanceFilterDI,
      month: dayjs(selectedInternalConnectedDocumentIncoming?.dateIssue).format(
        'YYYY-MM-DD'
      ),
    })
  }, [selectedInternalConnectedDocumentIncoming])

  const [advanceFilterSD, setAdvanceFilterSD] = useState({
    date_by: null,
    month: '',
    tag: [],
  })

  const [advanceFilterED, setAdvanceFilterED] = useState({
    date_by: null,
    month: '',
    tag: [],
  })

  const handleChangeIncomingDate = value => {
    setAdvanceFilterID({
      ...advanceFilterID,
      month: value,
    })
    formik.setFieldValue('outgoing_date', value)
  }

  const handleChangeDateIssued = value => {
    setAdvanceFilterDI({
      ...advanceFilterDI,
      month: value,
    })
    formik.setFieldValue('date_issued', value)
  }

  const handleChangeStartDate = value => {
    setAdvanceFilterSD({
      ...advanceFilterSD,
      month: value,
    })
    setStartDate(value)
  }

  const handleChangeEndDate = value => {
    setAdvanceFilterED({
      ...advanceFilterED,
      month: value,
    })
    setEndDate(value)
  }

  const [selectedOption, setSelectedOption] = useState('')
  const handleSelectChange = e => {
    setSelectedOption(e.target.value)
  }
  const handleClear = () => {
    setSelectedOption('')
  }
  const [bookGroupId, setBookGroupId] = useState()
  const [bookId, setBookId] = useState()
  useEffect(() => {
    ;(async () => {
      loadingAnimationStore.showSpinner(true)
      try {
        authorityStore.setAuthorityIssuedsQueryParams({
          type: DOCUMENT_TYPE.INCOMING,
          key: null,
        })
        await authorityStore.getAuthorityIssueds()
        const responseGetBookGroup = await bookStore.getBookGroup('DEN')
        const responseBookByBookGroupId = await bookStore.getBookByBookGroupId(
          responseGetBookGroup.data[0].id
        )
        formik.setFieldValue(
          'book_group_id',
          responseGetBookGroup.data?.length > 0
            ? responseGetBookGroup.data[0].id
            : null
        )
        setBookGroupId(responseGetBookGroup.data[0]?.id)
        formik.setFieldValue(
          'book_id',
          responseBookByBookGroupId.data?.length > 0
            ? responseBookByBookGroupId.data[0]?.id
            : null
        )
        setBookId(responseBookByBookGroupId.data[0]?.id)
      } catch (err) {
        createMessage(err?.vi || 'Đã có lỗi xảy ra!', true, 'error')
      } finally {
        loadingAnimationStore.showSpinner(false)
      }
    })()
    return () => {
      bookStore.clearStore()
      signedDocumentStore.clearFilePDFSign()
      selectPeopleStore.clearSelectPeopleStore()
    }
  }, [])

  const authorityIssuedId = authorityIssuedsList.map(
    authorityIssued => authorityIssued.id
  )

  useEffect(() => {
    if (authorityIssuedsList.length === 0)
      formik.setFieldValue('authority_issued_id', null)
    formik.setFieldValue('authority_issued_id', authorityIssuedId[0])
  }, [authorityIssuedsList])

  const [isVisibleAuthoritiesActionModal, setIsVisbleAuthoritiesActionModal] =
    useState(false)

  const handleToggleAuthoritiesActionModal = bool => event => {
    setIsVisbleAuthoritiesActionModal(bool)
  }

  useEffect(() => {
    formik.setFieldValue('urgency_level', selectedOption)
  }, [selectedOption])

  const handleCloseForm = async () => {
    handleClose()
    setButtonBlock(false)
    const responseGetBookGroup = await bookStore.getBookGroup('DEN')
    await bookStore.getBookByBookGroupId(responseGetBookGroup.data[0].id)
    formik.setErrors({})
    formik.setTouched({})
    formik.setFieldValue('book_group_id', bookGroupId)
    formik.setFieldValue('book_id', bookId)
    formik.setFieldValue('document_number', null)
    formik.setFieldValue('incoming_number', null)
    formik.setFieldValue('title', null)
    formik.setFieldValue('outgoing_date', null)
    formik.setFieldValue('date_issued', null)
    formik.setFieldValue('signer', null)
    formik.setFieldValue('urgency_level', null)
    formik.setFieldValue('authority_issued_id', authorityIssuedId[0] || null)
    formik.setFieldValue('attachments', null)
    formik.setFieldValue('assign_user', {
      assignees: [],
      start_date: null,
      end_date: null,
    })
    setStartDate(null)
    setEndDate(null)
    setSelectedOption('')
    setAdvanceFilterID({ ...advanceFilterID, month: '' })
    setAdvanceFilterDI({ ...advanceFilterDI, month: '' })
    setAdvanceFilterSD({ ...advanceFilterSD, month: '' })
    setAdvanceFilterED({ ...advanceFilterED, month: '' })
    setFileList([])
    setHandlerList([])
    setCombinerList([])
    setFollowerList([])
    clearSelectPeopleStore()
    clearSelected()
  }

  const handleChangeFile = info => {
    if (info.fileList.length === 0) {
      formik.setFieldValue('file_upload', undefined)
      setFileList([])
      return
    }
    const filteredFileList = info.fileList.filter(
      (elem, index, fileList) =>
        fileList.findIndex(file => file.name === elem.name) === index
    )
    setFileList(filteredFileList)
  }

  const handleRemoveFileFromUploadList = useCallback(
    fileUID => {
      const newFileList = fileList.filter(file => file.uid !== fileUID)
      setFileList(newFileList)
      if (newFileList.length === 0) {
        formik.setFieldValue('attachments', [])
      }
    },
    [fileList]
  )

  const handleSignSavis = (file, index) => {
    loadingAnimationStore.showSpinner(true)
    const formData = new FormData()
    formData.append('file', file.originFileObj)
    fileStore
      .uploadFile(formData)
      .then(res => {
        const dataSubmit = {
          image: '',
          reason: 'Signed',
          location: 'VietNam',
          provider: DIGITAL_SIGN_PROVIDER,
          contactInfo: '',
          type: DIGITAL_TYPE_SIGN_SYSTEM,
          isVisible: 1,
          page: 1,
          llx: 0,
          lly: 790,
          urx: 300,
          ury: 750,
          listSignFile: [
            {
              fileId: res.data.file_id,
            },
          ],
        }
        signedDocumentStore
          .signDocumentSavis(dataSubmit)
          .then(res => {
            const { fileSignId, fileOriginName, fileSignName } =
              res.data.listSignFile[0]
            const newFileList = [...fileList]
            newFileList[index] = {
              name: fileSignName,
              id: fileSignId,
              uid: file.uid,
              isSigned: true,
              type: 'application/pdf',
            }
            setFileList(newFileList)
            loadingAnimationStore.showSpinner(false)
            createMessage(
              `Văn bản "${fileOriginName}" đã được kí`,
              true,
              'success'
            ).then()
          })
          .catch(err => {
            loadingAnimationStore.showSpinner(false)
            return createMessage(err?.vi || 'Ký số thất bại!', true, 'error')
          })
      })
      .catch(() => loadingAnimationStore.showSpinner(false))
  }

  const handleBeforeSign = (file, index) => {
    setDataSignSavis({
      file: file,
      index: index,
    })
  }

  const [isRenameFileFromLTVBNB, setIsRenameFileFromLTVBNB] = useState(false)
  const [fileExt, setFileExt] = useState(undefined)
  const [fileTargetRename, setFileTargetRename] = useState(null)
  const [isModalVisibleRenameFile, setIsModalVisibleRenameFile] =
    useState(false)
  const [fieldsRenameFile, setFieldsRenameFile] = useState([
    {
      name: ['rename'],
      value: '',
    },
  ])
  const handleOpenPopupRenameFile = (file, index) => {
    setFieldsRenameFile([
      {
        name: ['rename'],
        value: utils.removeExtensionFile(file.name),
      },
    ])
    setFileExt('.' + utils.getExtensionFile(file.name))
    setFileTargetRename({
      ...file,
      index: index,
    })
    setIsModalVisibleRenameFile(true)
  }

  const renameFile = (originalFile, newName) => {
    return new File([originalFile], newName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    })
  }

  const submitRenameFile = async value => {
    const index = fileTargetRename?.index
    // đổi tên file nhận từ thiết bị (file chưa ký)
    if (fileTargetRename.originFileObj) {
      const newFileList = [...fileList]
      newFileList[index].name = value.rename + fileExt
      newFileList[index].originFileObj = renameFile(
        newFileList[index].originFileObj,
        newFileList[index].name
      )
      setFileList(newFileList)
      setIsModalVisibleRenameFile(false)
      createMessage('Đổi tên file thành công', true, 'success')
    }
    // đổi tên file nhận từ thiết bị (file đã ký)
    if (!fileTargetRename.originFileObj) {
      await fileStore.handleRenameFile(
        fileTargetRename.id,
        value.rename + fileExt
      )
      setIsModalVisibleRenameFile(false)
      const newFileList = [...fileList]
      newFileList[index].name = value.rename + fileExt
      setFileList(newFileList)
      createMessage('Đổi tên file thành công', true, 'success')
    }
  }

  const handleRemoveFileFromDocumentList = file_id => {
    const newFileList = fileFromDocumentList.filter(
      file => file.file_id !== file_id
    )
    setFileFromDocumentList(newFileList)
  }
  const { responseSign } = signedDocumentStore
  const [dataSignSavis, setDataSignSavis] = useState()

  useEffect(() => {
    if (!responseSign) return
    const { fileSignId, fileSignName } = responseSign.data.listSignFile[0]
    const newFileList = [...fileList]
    newFileList[dataSignSavis.index] = {
      name: fileSignName,
      id: fileSignId,
      uid: dataSignSavis.file.uid,
      isSigned: true,
      type: 'application/pdf',
    }
    setFileList(newFileList)
  }, [responseSign])

  const [warningSH, setWarningSH] = useState({})

  const validateDocumentNumber = async e => {
    formik.handleChange(e)
    if (e.target.value && e.target.value.trim() === '') {
      return Promise.reject('Cần nhập ký tự, vui lòng không bỏ trống')
    } else {
      try {
        const response = await internalDocumentStore.getWarningDocumentNumber(
          e.target.value,
          'INCOMING'
        )
        if (response.data.status === 'WARNING') {
          setWarningSH(response.data)
          return Promise.reject(response.data.message)
        } else {
          setWarningSH(response.data)
          return Promise.resolve()
        }
      } catch (error) {
        console.log(error)
        createMessage(error?.vi || 'Đã có lỗi xảy ra!', true, 'error')
      }
    }
  }

  const [buttonBlock, setButtonBlock] = useState(false)
  const handleOpenSelectOne = () => {
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.HANDLER)
    setOnlyOneUser('onlyOneUser')
  }
  const handleOpenSelectNTH = () => {
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.COMBINER)
    setOnlyOneUser('')
  }
  const handleOpenSelectNTD = () => {
    handleOpenSelectUserDepartmentPopup(ASSIGNEE_TYPE.FOLLOWER)
    setOnlyOneUser('')
  }
  return (
    <Dialog
      open={open}
      onClose={handleCloseForm}
      scroll={'paper'}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      className="doc-popup"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100vw',
            maxWidth: '100vw',
            mx: 0,
            zIndex: 50,
          },
        },
      }}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          id="scroll-dialog-title"
          className="text-xl font-bold text-[#ffffff]"
          sx={{
            fontWeight: 700,
            backgroundColor: '#2C65AC',
          }}>
          <span className="font-bold">Tạo mới văn bản đến</span>
        </DialogTitle>
        <DialogContent>
          <div>
            <FormControl variant="standard" sx={{ mt: 1, minWidth: '100%' }}>
              <InputLabel>
                <span
                  style={{
                    display: 'inline-block',
                    color: '#ff4d4f',
                    fontSize: '14px',
                    fontFamily: 'SimSun,sans-serif',
                    lineHeight: 1,
                  }}>
                  *
                </span>
                <span> Nhóm sổ văn bản</span>
              </InputLabel>
              <Select
                name="book_group_id"
                sx={{ width: '100%' }}
                defaultValue={bookGroupList[0]?.id}
                onChange={event => {
                  const bookId = event.target.value
                  handleSelectBookGroupId(bookId)
                }}
                label="Nhóm sổ văn bản">
                {bookGroupList.map(book => (
                  <MenuItem key={book.id} value={book.id}>
                    {book.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl
              variant="standard"
              sx={{ mt: 1, mb: 1, minWidth: '100%' }}>
              <InputLabel>
                <span
                  style={{
                    display: 'inline-block',
                    color: '#ff4d4f',
                    fontSize: '14px',
                    fontFamily: 'SimSun,sans-serif',
                    lineHeight: 1,
                  }}>
                  *
                </span>
                <span> Sổ văn bản</span>
              </InputLabel>
              <Select
                name="book_id"
                sx={{ width: '100%' }}
                defaultValue={bookListOfGroup[0]?.id}
                onChange={event => {
                  const bookId = event.target.value
                  handleSelectBookId(bookId)
                }}
                label="Sổ văn bản">
                {bookListOfGroup.map(book => (
                  <MenuItem key={book.id} value={book.id}>
                    {book.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={{ marginTop: 1 }}>
            <TextField
              name="document_number"
              id="standard-basic"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Số hiệu</span>
                </>
              }
              variant="standard"
              sx={{ textAlign: 'center', height: '56px', width: '100%' }}
              onChange={e => {
                validateDocumentNumber(e)
              }}
              error={
                (formik.errors.document_number &&
                  formik.touched.document_number) ||
                warningSH?.status === 'WARNING'
                  ? true
                  : false
              }
            />
          </div>
          {warningSH?.status === 'WARNING' && (
            <span style={{ color: 'red' }}>
              Số hiệu văn bản đã sử dụng, vui lòng nhập số hiệu khác!
            </span>
          )}
          <div
            style={{
              marginBottom: '4px',
              marginTop: warningSH?.status === 'WARNING' && '10px',
            }}>
            <TextField
              name="incoming_number"
              id="standard-basic"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Số đến</span>
                </>
              }
              variant="standard"
              sx={{ textAlign: 'center', height: '60px', width: '100%' }}
              onChange={formik.handleChange}
              error={
                formik.errors.incoming_number && formik.touched.incoming_number
                  ? true
                  : false
              }
            />
          </div>
          <div>
            <TextField
              name="title"
              id="outlined-multiline-static"
              label={
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Trích yếu</span>
                </>
              }
              multiline
              rows={3}
              // variant="filled"
              sx={{ width: '100%' }}
              value={formik.values?.title}
              onChange={formik.handleChange}
              error={formik.errors.title && formik.touched.title ? true : false}
            />
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="outgoing_date"
                label={
                  <>
                    <span
                      style={{
                        display: 'inline-block',
                        color: '#ff4d4f',
                        fontSize: '14px',
                        fontFamily: 'SimSun,sans-serif',
                        lineHeight: 1,
                      }}>
                      *
                    </span>
                    <span> Ngày đến</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={formik.values?.outgoing_date}
                onChange={handleChangeIncomingDate}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={
                      formik.errors.outgoing_date &&
                      formik.touched.outgoing_date
                        ? true
                        : false
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="date_issued"
                label={
                  <>
                    <span> Ngày ban hành</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={formik.values?.date_issued}
                onChange={handleChangeDateIssued}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={false}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ marginTop: '12px' }}>
            <TextField
              name="signer"
              id="outlined-multiline-static"
              label=" Người ký"
              // variant="filled"
              sx={{ width: '100%' }}
              value={formik.values?.signer}
              onChange={formik.handleChange}
            />
          </div>
          <div>
            <FormControl
              variant="outlined"
              sx={{ mt: '12px', minWidth: '100%' }}>
              <InputLabel>
                <>
                  <span> Độ khẩn</span>
                </>
              </InputLabel>
              <Select
                name="urgency_level"
                sx={{ width: '100%' }}
                onChange={handleSelectChange}
                label="Độ khẩn"
                value={selectedOption}>
                <MenuItem value={''}>Bỏ chọn độ khẩn</MenuItem>
                <MenuItem value={'001'}>Khẩn</MenuItem>
                <MenuItem value={'002'}>Hỏa tốc</MenuItem>
                <MenuItem value={'003'}>Hỏa tốc hẹn giờ</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ display: 'flex' }}>
            <FormControl variant="outlined" sx={{ mt: '12px', width: '100%' }}>
              <InputLabel>
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '14px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                  <span> Cơ quan ban hành</span>
                </>
              </InputLabel>
              <Select
                name="authority_issued_id"
                sx={{ width: '100%' }}
                defaultValue={authorityIssuedsList[0]?.id}
                onChange={formik.handleChange}
                label="Cơ quan ban hành">
                {authorityIssuedsList.map(authorityIssued => (
                  <MenuItem key={authorityIssued.id} value={authorityIssued.id}>
                    {authorityIssued.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              style={{
                color: '#fff',
                backgroundColor: '#1677ff',
                marginTop: '12px',
                marginLeft: '5px',
                width: '91px',
                height: '56px',
              }}
              onClick={handleToggleAuthoritiesActionModal(true)}>
              <span style={{ fontSize: '15px' }}>Tạo mới</span>
            </Button>
          </div>
          <div style={{ marginTop: '10px' }}>
            <Upload
              valuePropName={'fileList'}
              fileList={fileList}
              multiple={true}
              beforeUpload={() => false}
              onChange={handleChangeFile}
              showUploadList={false}>
              <Button
                danger={
                  formik.errors.attachments && formik.touched.attachments
                    ? true
                    : false
                }
                style={{
                  color:
                    formik.errors.attachments && formik.touched.attachments
                      ? null
                      : '#fff',
                  backgroundColor:
                    formik.errors.attachments && formik.touched.attachments
                      ? null
                      : '#1677ff',
                  fontSize: '15px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                icon={<UploadOutlined />}>
                <>
                  <span>Chọn tài liệu đính kèm </span>
                  <span
                    style={{
                      display: 'inline-block',
                      color: '#ff4d4f',
                      fontSize: '18px',
                      fontFamily: 'SimSun,sans-serif',
                      lineHeight: 1,
                    }}>
                    *
                  </span>
                </>
              </Button>
            </Upload>
          </div>
          {
            <UploadFileListWrapper>
              {fileList.map((file, index) => (
                <UploadFileListItem
                  key={file.uid || file.id}
                  isSigned={file.isSigned}
                  isNotPDF={file.type !== 'application/pdf'}>
                  <FileTextOutlined />
                  <span>{file.name}</span>
                  {file.isSigned ? (
                    <Button
                      className={'digital-signature'}
                      type="text"
                      onClick={() => createMessage('Đã ký!', true, 'info')}>
                      Đã ký
                    </Button>
                  ) : file.type !== 'application/pdf' ? (
                    <Button
                      className={'digital-signature'}
                      type="text"
                      onClick={() =>
                        createMessage(
                          'Chỉ ký được file PDF, vui lòng chọn file khác!',
                          true,
                          'info'
                        )
                      }>
                      Ký số
                    </Button>
                  ) : (
                    <Popconfirm
                      placement="top"
                      title={'Hãy chọn loại ký'}
                      cancelText="Ký mặc đinh"
                      onCancel={() => handleSignSavis(file, index)}>
                      <Button
                        className={'digital-signature'}
                        type="text"
                        onClick={() => handleBeforeSign(file, index)}>
                        Ký số
                      </Button>
                    </Popconfirm>
                  )}
                  {
                    <Button
                      className={'rename'}
                      type="text"
                      onClick={() => {
                        setIsRenameFileFromLTVBNB(false)
                        handleOpenPopupRenameFile(file, index)
                      }}>
                      Đổi tên
                    </Button>
                  }
                  <Tooltip title={'Xoá tập tin'}>
                    <DeleteOutlined
                      onClick={() =>
                        handleRemoveFileFromUploadList(file.uid, index)
                      }
                    />
                  </Tooltip>
                </UploadFileListItem>
              ))}
              {fileFromDocumentList?.map((file, index) => (
                <UploadFileListItem key={file.fileId}>
                  <FileTextOutlined style={{ color: '1890FF' }} />
                  <span style={{ color: '#1890FF' }}>{file.name}</span>
                </UploadFileListItem>
              ))}
            </UploadFileListWrapper>
          }
          <div
            style={{
              display: 'flex',
            }}>
            <InfoOutlinedIcon style={{ margin: '-4px 5px 0 5px' }} />
            <Title level={4} style={{ margin: '-8px 0 5px 0' }}>
              Thông tin phân phát
            </Title>
          </div>
          <div style={{ display: 'flex' }}>
            <label style={{ margin: '5px' }}>Lãnh đạo xử lý:</label>
            <div>
              <Button
                style={{
                  color: '#ffb100',
                  margin: '5px',
                  border: '1px solid blue',
                }}
                onClick={() => handleOpenSelectOne()}>
                <AiOutlineUser />
              </Button>
              <div>
                {handlerList.map(el => (
                  <SelectList>
                    <Tag
                      className={'tag-selected'}
                      color={'blue'}
                      key={el.id}
                      onClick={() => selectPeopleStore.handleRemoveSelect(el)}>
                      <AvatarHasName
                        imgId={el.image_id}
                        size={22}
                        name={el.full_name}
                        icon={
                          (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                            <AiFillBank />
                          )) ||
                          (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                            <AiFillBank />
                          ))
                        }
                      />
                    </Tag>
                  </SelectList>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <label style={{ margin: '5px' }}>Người xử lý:</label>
            <div>
              <Button
                style={{
                  color: '#ffb100',
                  margin: '5px',
                  border: '1px solid blue',
                }}
                onClick={() => handleOpenSelectNTH()}>
                <AiOutlineUser />
              </Button>
              <Button
                style={{
                  color: '#ffb100',
                  margin: '5px',
                  border: '1px solid blue',
                }}
                onClick={() =>
                  handleOpenSelectGroupPopup(ASSIGNEE_TYPE.COMBINER)
                }>
                <AiOutlineTeam />
              </Button>
              <div>
                {combinerList.map(el => (
                  <SelectList>
                    <Tag
                      className={'tag-selected'}
                      color={'blue'}
                      key={el.id}
                      onClick={() => selectPeopleStore.handleRemoveSelect(el)}>
                      <AvatarHasName
                        imgId={el.image_id}
                        size={22}
                        name={el.full_name}
                        icon={
                          (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                            <AiFillBank />
                          )) ||
                          (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                            <AiFillBank />
                          ))
                        }
                      />
                    </Tag>
                  </SelectList>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            <label style={{ margin: '5px' }}>Phối hợp/Theo dõi:</label>
            <div>
              <Button
                style={{
                  color: '#ffb100',
                  margin: '5px',
                  border: '1px solid blue',
                }}
                onClick={() => handleOpenSelectNTD()}>
                <AiOutlineUser />
              </Button>
              <Button
                style={{
                  color: '#ffb100',
                  margin: '5px',
                  border: '1px solid blue',
                }}
                onClick={() =>
                  handleOpenSelectGroupPopup(ASSIGNEE_TYPE.FOLLOWER)
                }>
                <AiOutlineTeam />
              </Button>
              <div>
                {followerList.map(el => (
                  <SelectList>
                    <Tag
                      className={'tag-selected'}
                      color={'blue'}
                      key={el.id}
                      onClick={() => selectPeopleStore.handleRemoveSelect(el)}>
                      <AvatarHasName
                        imgId={el.image_id}
                        size={22}
                        name={el.full_name}
                        icon={
                          (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                            <AiFillBank />
                          )) ||
                          (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                            <AiFillBank />
                          ))
                        }
                      />
                    </Tag>
                  </SelectList>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="start_date"
                label={
                  <>
                    <span> Ngày bắt đầu</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={advanceFilterSD.month}
                onChange={handleChangeStartDate}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={false}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ marginTop: '12px' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
              <DatePicker
                name="end_date"
                label={
                  <>
                    <span> Ngày kết thúc</span>
                  </>
                }
                openTo="day"
                views={['day', 'month', 'year']}
                value={advanceFilterED.month}
                onChange={handleChangeEndDate}
                inputFormat="DD/MM/YYYY"
                renderInput={params => (
                  <TextField
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                    {...params}
                    error={false}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
        </DialogContent>
        <DialogActions
          sx={{ backgroundColor: '#e3e4e9', position: 'sticky', bottom: 0 }}>
          <Button
            sx={{ fontWeight: 600 }}
            onClick={handleCloseForm}
            className="w-[94px] h-[40px] text-sm font-semibold leading-5 text-primary">
            ĐÓNG
          </Button>
          {buttonBlock || warningSH?.status === 'WARNING' ? (
            <button
              disabled={true}
              sx={{ fontWeight: 600 }}
              className="opacity-[0.3] uppercase w-[94px] h-[40px] rounded-[100px] text-sm font-semibold leading-5 text-white bg-[#0073C7]">
              {' '}
              Tạo mới
            </button>
          ) : (
            <button
              type="submit"
              sx={{ fontWeight: 600 }}
              className="uppercase w-[94px] h-[40px] rounded-[100px] text-sm font-semibold leading-5 text-white bg-[#0073C7]">
              {' '}
              Tạo mới
            </button>
          )}
        </DialogActions>
      </form>
      <SelectPeoplePopup
        onlyOneUser={onlyOne}
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
      <SelectGroupPopup
        isVisibleSelectGroupPopup={isVisibleSelectGroupPopup}
        handleCancelSelectGroup={handleCancelSelectGroup}
        handleSubmitSelectGroup={() => setIsVisibleSelectGroupPopup(false)}
      />
      <PopupRenameFile
        isModalVisibleRenameFile={isModalVisibleRenameFile}
        handleCloseModalRenameFile={() => setIsModalVisibleRenameFile(false)}
        submitRenameFile={submitRenameFile}
        fieldsRenameFile={fieldsRenameFile}
        fileExt={fileExt}
      />
      {isVisibleAuthoritiesActionModal && (
        <AuthoritiesActionModal
          disabledAuthoritiesTypePicker
          tabActive={DOCUMENT_TYPE.INCOMING}
          payloadModal={{
            status: true,
            isCreate: true,
            updatePayLoad: null,
          }}
          handleCloseModal={handleToggleAuthoritiesActionModal(false)}
        />
      )}
    </Dialog>
  )
}

export default memo(
  inject(
    'authorityStore',
    'selectPeopleStore',
    'bookStore',
    'loadingAnimationStore',
    'commonStore',
    'fileStore',
    'signedDocumentStore',
    'internalDocumentStore'
  )(observer(InternalConnectedDocumentIncomingCreate))
)
